import { useMemo, useState } from "react";
import { ManageLessonRow } from "./ManageLessonRow.js";
import { scheduling } from "./scheduling.js";
import { futureLessons, staffBooking } from "./staff.js";
import { TutorBookLesson } from "./TutorBookLesson.js";
import { useAsync } from "../useAsync.js";

export function OfficeTutorBookingPanel({
  studentId,
  studentName,
  onChanged,
}: {
  studentId: string;
  studentName: string;
  onChanged: () => void;
}) {
  const tutors = useAsync(() => scheduling.tutors(), []);
  const future = useAsync(() => futureLessons(studentId), [studentId]);
  const credits = useAsync(() => staffBooking.lessonCredits(studentId), [studentId]);
  const [selectedTutorId, setSelectedTutorId] = useState("");

  const selectedTutor = useMemo(
    () => tutors.data?.find((tutor) => tutor.tutorProfileId === selectedTutorId) ?? null,
    [selectedTutorId, tutors.data],
  );
  const selectedLessons = useMemo(
    () =>
      selectedTutor === null
        ? []
        : (future.data?.lessons ?? []).filter((lesson) => lesson.tutorId === selectedTutor.tutorProfileId),
    [future.data, selectedTutor],
  );
  const selectedBlocks = useMemo(
    () =>
      selectedTutor === null
        ? []
        : (credits.data?.blocks ?? []).filter((block) => block.provider_id === selectedTutor.providerId),
    [credits.data, selectedTutor],
  );
  /**
   * Lessons belonging to no tutor at all, which the dropdown can never reach.
   *
   * Every tutor in that list has a profile id, so a lesson whose `tutorId` is
   * null matches none of them and is filtered out of every selection. The
   * student page hides its own row management from anybody who can manage other
   * tutors, on the understanding that this panel is where they act — so between
   * the two, an unattached lesson could otherwise be manageable by an ordinary
   * tutor and not by an owner/office user. Shown here on their own, outside the
   * selection, so the most privileged account is not the one that cannot act.
   *
   * They are exactly what a detached record leaves behind, which is how #176's
   * eighteen drum lessons looked, so this is not a hypothetical row.
   */
  const unassignedLessons = useMemo(
    () => (future.data?.lessons ?? []).filter((lesson) => lesson.tutorId === null),
    [future.data],
  );

  function refresh() {
    future.reload();
    credits.reload();
    onChanged();
  }

  return (
    <section aria-labelledby="office-booking-heading">
      <h2 id="office-booking-heading">Manage lessons for a tutor</h2>
      <div className="panel">
        <label>
          Tutor
          <select
            value={selectedTutorId}
            onChange={(event) => setSelectedTutorId(event.target.value)}
            disabled={tutors.loading}
          >
            <option value="">Select a tutor…</option>
            {(tutors.data ?? []).map((tutor) => (
              <option key={tutor.tutorProfileId} value={tutor.tutorProfileId}>
                {tutor.displayName}
              </option>
            ))}
          </select>
        </label>
        <p className="small muted">
          The selected tutor is the target of the booking action. Your signed-in account remains the actor recorded by the booking system.
        </p>
        {tutors.error && <div className="error">We could not load the tutors you may act for.</div>}
      </div>

      {selectedTutor && (
        <>
          {/* Keyed on the tutor, so changing the dropdown gives a fresh form.
              Without it the component kept its own `done` confirmation across
              the change, and after one booking the panel sat showing a success
              message for the previous tutor with no way back but a reload.
              `CalendarBookLesson` keys it for the same reason. */}
          <TutorBookLesson
            key={selectedTutor.tutorProfileId}
            studentId={studentId}
            studentName={studentName}
            blocks={selectedBlocks}
            selectedTutorProviderId={selectedTutor.providerId}
            onBooked={refresh}
          />

          <h3>Upcoming lessons with {selectedTutor.displayName}</h3>
          <div className="panel">
            {future.loading && <p className="empty">Loading lessons…</p>}
            {future.error && <div className="error">We could not load this student&apos;s future lessons.</div>}
            {!future.loading && !future.error && selectedLessons.length === 0 && (
              <p className="empty">No future lessons with this tutor.</p>
            )}
            {selectedLessons.map((lesson) => (
              <ManageLessonRow
                key={lesson.ameliaAppointmentId}
                appointmentId={lesson.ameliaAppointmentId}
                canManage
                studentName={studentName}
                startsAt={lesson.startsAt}
                serviceName={lesson.serviceName}
                onBehalf={{
                  studentId,
                  endsAt: lesson.endsAt,
                  status: lesson.status,
                  sharedWith: lesson.sharedWith,
                  tutorId: lesson.tutorId,
                  tutorName: lesson.tutorName,
                  externalProviderId: selectedTutor.providerId,
                }}
                onChanged={refresh}
              >
                <div className="small muted">
                  {lesson.serviceName ?? "Lesson"} · {lesson.status}
                </div>
              </ManageLessonRow>
            ))}
          </div>
        </>
      )}

      {unassignedLessons.length > 0 && (
        <>
          <h3>Lessons with no tutor</h3>
          <p className="small muted">
            Booked ahead but attached to nobody, so they appear under no tutor above. Usually a
            record that has come apart — worth putting right as well as acting on here.
          </p>
          <div className="panel">
            {unassignedLessons.map((lesson) => (
              <ManageLessonRow
                key={lesson.ameliaAppointmentId}
                appointmentId={lesson.ameliaAppointmentId}
                canManage
                studentName={studentName}
                startsAt={lesson.startsAt}
                serviceName={lesson.serviceName}
                onBehalf={{
                  studentId,
                  endsAt: lesson.endsAt,
                  status: lesson.status,
                  sharedWith: lesson.sharedWith,
                  tutorId: null,
                  tutorName: null,
                  externalProviderId: null,
                }}
                onChanged={refresh}
              >
                <div className="small muted">
                  {lesson.serviceName ?? "Lesson"} · {lesson.status}
                </div>
              </ManageLessonRow>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
